import { useEffect, useLayoutEffect, useRef } from "react";
import { useTheme } from "next-themes";
import { restoreDefaultStatusBar, setStatusBarLight } from "@/lib/splash";

/**
 * Keeps the document in the light theme while the calling surface is mounted.
 *
 * DARK_MODE.md: the check-in kiosk is always light. Lobbies are bright, and the
 * printed labels must match the screen. The theme is a class on <html> (next-themes,
 * attribute="class"), so a wrapper element cannot reset it for portalled dialogs;
 * the class itself has to change. A MutationObserver re-strips `dark` if next-themes
 * re-applies it (a system colour-scheme change, or a stored preference landing late).
 *
 * Runs in a layout effect so the first committed frame is already light; mount it in
 * an eager wrapper above any Suspense boundary so the lazy-load fallback is light too.
 *
 * `enabled` lets a route-aware caller above the router outlet toggle the enforcement as
 * the location changes; when it flips off the cleanup below restores the theme.
 *
 * On unmount the provider's latest resolved theme is restored, not the snapshot taken
 * at mount: while this hook holds <html> light, a provider write of "light" is a no-op
 * the DOM cannot reveal, so a system theme change mid-session is only visible through
 * `resolvedTheme`. The mount-time snapshot is the fallback when there is no provider.
 */
export function useForcedLightTheme(enabled = true): void {
  const { resolvedTheme } = useTheme();
  const latestTheme = useRef(resolvedTheme);
  useEffect(() => {
    latestTheme.current = resolvedTheme;
  }, [resolvedTheme]);

  useLayoutEffect(() => {
    if (!enabled) return undefined;
    const root = document.documentElement;
    const hadDark = root.classList.contains("dark");
    const hadLight = root.classList.contains("light");
    const previousColorScheme = root.style.colorScheme;

    const apply = () => {
      if (root.classList.contains("dark")) root.classList.remove("dark");
      if (!root.classList.contains("light")) root.classList.add("light");
      if (root.style.colorScheme !== "light") root.style.colorScheme = "light";
    };

    // Mobile browser chrome follows <meta name="theme-color">; keep it light too. index.html's
    // pre-paint script may already have swapped it, leaving the real value in data-default-content.
    const themeMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (themeMeta) {
      if (themeMeta.dataset.defaultContent === undefined) {
        themeMeta.dataset.defaultContent = themeMeta.content;
      }
      themeMeta.content = "#ffffff";
    }

    // iOS standalone (Home Screen) web apps take their status bar from this tag instead.
    const statusBarMeta = document.querySelector<HTMLMetaElement>(
      'meta[name="apple-mobile-web-app-status-bar-style"]',
    );
    if (statusBarMeta) {
      if (statusBarMeta.dataset.defaultContent === undefined) {
        statusBarMeta.dataset.defaultContent = statusBarMeta.content;
      }
      statusBarMeta.content = "default";
    }

    // The Capacitor shell ignores the meta tag; the kiosk has no AppLayout to set the native bar.
    void setStatusBarLight().catch(() => undefined);

    apply();
    const observer = new MutationObserver(apply);
    observer.observe(root, { attributes: true, attributeFilter: ["class", "style"] });

    return () => {
      observer.disconnect();
      void restoreDefaultStatusBar().catch(() => undefined);
      if (themeMeta && themeMeta.dataset.defaultContent !== undefined) {
        themeMeta.content = themeMeta.dataset.defaultContent;
        delete themeMeta.dataset.defaultContent;
      }
      if (statusBarMeta && statusBarMeta.dataset.defaultContent !== undefined) {
        statusBarMeta.content = statusBarMeta.dataset.defaultContent;
        delete statusBarMeta.dataset.defaultContent;
      }
      const latest = latestTheme.current;
      if (latest === "dark" || latest === "light") {
        root.classList.toggle("dark", latest === "dark");
        root.classList.toggle("light", latest === "light");
        root.style.colorScheme = latest;
        return;
      }
      root.classList.toggle("dark", hadDark);
      root.classList.toggle("light", hadLight);
      root.style.colorScheme = previousColorScheme;
    };
  }, [enabled]);
}
